import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { Check, X, Download, AlertTriangle, Eye } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/Card';
import Modal from '@/components/Modal';
import { StatusBadge } from '@/components/Badge';
import Loader from '@/components/Loader';
import Pagination from '@/components/Pagination';
import { leaveService, fileUrl } from '@/services';
import { fmtDate, download, fmtTime12 } from '@/utils/format';

export default function AdminLeaves() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const token = useSelector((s) => s.auth.token);
  const [filters, setFilters] = useState({ status: 'pending', type: '' });
  const [page, setPage] = useState(1);
  const [rejecting, setRejecting] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ['leaves', 'admin', filters, page],
    queryFn: () => leaveService.adminList({ ...filters, page, limit: 15 }),
  });

  const decide = useMutation({
    mutationFn: ({ id, status, adminNote }) => leaveService.decide(id, { status, adminNote }),
    onSuccess: () => {
      toast.success('Updated');
      qc.invalidateQueries({ queryKey: ['leaves'] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Leave Requests"
        subtitle="Review and approve employee leave requests"
        actions={
          <button className="btn-secondary" onClick={() => download('reports/admin/leaves.csv', 'leaves.csv', token)}>
            <Download size={14} /> Export CSV
          </button>
        }
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <select className="input w-44" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select className="input w-44" value={filters.type} onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))}>
            <option value="">All types</option>
            <option value="sick">Sick</option>
            <option value="casual">Casual</option>
            <option value="paid">Paid</option>
            <option value="wfh">WFH</option>
            <option value="half-day">Half-day</option>
            <option value="short">Short leave</option>
          </select>
        </div>

        {isLoading ? (
          <Loader />
        ) : (
          <div className="table-wrap">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="th">Employee</th>
                  <th className="th">Type</th>
                  <th className="th">When</th>
                  <th className="th">Duration</th>
                  <th className="th">Reason</th>
                  <th className="th">Status</th>
                  <th className="th">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(data?.items || []).length === 0 && (
                  <tr><td colSpan={7} className="td text-center text-slate-500">No requests</td></tr>
                )}
                {(data?.items || []).map((l) => (
                  <tr
                    key={l._id}
                    onClick={() => navigate(`/leaves/${l._id}`)}
                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  >
                    <td className="td">
                      <div className="font-medium">{l.user?.name}</div>
                      <div className="text-xs text-slate-500">{l.user?.department}</div>
                    </td>
                    <td className="td capitalize">{l.type === 'short' ? 'Short leave' : l.type}</td>
                    <td className="td">
                      {l.type === 'short' ? (
                        <>
                          <div>{fmtDate(l.startDate)}</div>
                          <div className="text-xs text-slate-500">{fmtTime12(l.startTime)} – {fmtTime12(l.endTime)}</div>
                        </>
                      ) : (
                        <>
                          <div>{fmtDate(l.startDate)}</div>
                          <div className="text-xs text-slate-500">to {fmtDate(l.endDate)}</div>
                        </>
                      )}
                    </td>
                    <td className="td">
                      {l.type === 'short'
                        ? `${(l.hours || 0).toFixed(2)} hrs`
                        : `${l.days} day${l.days === 1 ? '' : 's'}`}
                    </td>
                    <td className="td max-w-xs">
                      <div className="truncate" title={l.reason}>{l.reason}</div>
                      {l.attachment && <a href={fileUrl(l.attachment)} target="_blank" rel="noreferrer" className="text-xs text-brand-600 underline">View attachment</a>}
                      {l.status === 'cancelled' && l.cancellationReason && (
                        <div className="text-[11px] text-rose-500 mt-1" title={l.cancellationReason}>
                          <span className="font-semibold">Cancelled:</span> {l.cancellationReason}
                        </div>
                      )}
                    </td>
                    <td className="td"><StatusBadge status={l.status} /></td>
                    <td className="td" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <button
                          className="btn-ghost p-1 text-slate-500 hover:text-brand-600"
                          title="View details"
                          onClick={() => navigate(`/leaves/${l._id}`)}
                        ><Eye size={16} /></button>
                        {l.status === 'pending' ? (
                          <>
                            <button
                              className="btn-ghost text-emerald-600 p-1"
                              title="Approve"
                              onClick={() => decide.mutate({ id: l._id, status: 'approved' })}
                            ><Check size={16} /></button>
                            <button
                              className="btn-ghost text-rose-600 p-1"
                              title="Reject"
                              onClick={() => setRejecting(l)}
                            ><X size={16} /></button>
                          </>
                        ) : (
                          <span className="text-xs text-slate-400 ml-1">{l.adminNote ? '' : '—'}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination page={page} limit={15} total={data?.total || 0} onChange={setPage} />
      </Card>

      {rejecting && (
        <RejectLeaveModal
          leave={rejecting}
          onClose={() => setRejecting(null)}
          onConfirmed={(note) => {
            decide.mutate(
              { id: rejecting._id, status: 'rejected', adminNote: note },
              { onSuccess: () => setRejecting(null) }
            );
          }}
          submitting={decide.isPending}
        />
      )}
    </div>
  );
}

function RejectLeaveModal({ leave, onClose, onConfirmed, submitting }) {
  const [reason, setReason] = useState('');
  const valid = reason.trim().length > 0;

  return (
    <Modal
      open
      onClose={onClose}
      title="Reject leave request"
      size="md"
      footer={
        <>
          <button onClick={onClose} className="btn-secondary" disabled={submitting}>Cancel</button>
          <button
            onClick={() => valid && onConfirmed(reason.trim())}
            className="btn-danger"
            disabled={!valid || submitting}
          >
            <X size={14} /> {submitting ? 'Rejecting...' : 'Reject leave'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex items-start gap-3 p-3 rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30">
          <AlertTriangle className="text-rose-600 shrink-0 mt-0.5" size={18} />
          <div className="text-sm text-rose-700 dark:text-rose-300">
            You're rejecting <b>{leave.user?.name}</b>'s {leave.type === 'short' ? 'short' : leave.type} leave. The employee will be notified along with your note.
          </div>
        </div>
        <div className="text-sm text-slate-600 dark:text-slate-300">
          <b>{fmtDate(leave.startDate)}</b>
          {leave.type !== 'short' && leave.endDate && (
            <> – <b>{fmtDate(leave.endDate)}</b></>
          )}
          {leave.type === 'short' && leave.startTime && leave.endTime && (
            <> · {fmtTime12(leave.startTime)} – {fmtTime12(leave.endTime)}</>
          )}
        </div>
        <div>
          <label className="label">Reason for rejection</label>
          <textarea
            rows={4}
            className="input"
            placeholder="Let the employee know why you're rejecting this request..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoFocus
          />
        </div>
      </div>
    </Modal>
  );
}
